import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock } from 'lucide-react';
import BasicScheduleTab from './BasicScheduleTab';
import AdvancedScheduleTab from './AdvancedScheduleTab';
import { DisplaySchedule, LegacySchedule, EnhancedSchedule } from '@/types/display';
import { detectTimezone } from '@/utils/timezoneUtils';

interface UnifiedScheduleSectionProps {
  schedule: DisplaySchedule;
  city?: string | null;
  state?: string | null;
  onChange: (schedule: DisplaySchedule) => void;
  defaultTab?: 'basic' | 'advanced';
  showAdvanced?: boolean; // Whether to show the advanced tab (default true)
}

/**
 * Unified Schedule Section Component
 *
 * Provides a tabbed interface for scheduling with:
 * - Basic Tab: Simple, user-friendly date picker, day selection, and hours
 * - Advanced Tab: Full-featured schedule editor with timezone, per-day hours, overrides
 *
 * Handles both Legacy and Enhanced schedule formats seamlessly.
 */
const UnifiedScheduleSection: React.FC<UnifiedScheduleSectionProps> = ({
  schedule,
  city,
  state,
  onChange,
  defaultTab = 'basic',
  showAdvanced = true,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>(defaultTab);

  // Type guard functions for schedule format detection
  const isEnhancedSchedule = (s: DisplaySchedule): s is EnhancedSchedule => {
    return s !== null && typeof s === 'object' && 'timezone' in s;
  };

  const isLegacySchedule = (s: DisplaySchedule): s is LegacySchedule => {
    return s !== null && typeof s === 'object' && 'start_date' in s;
  };

  // Determine current schedule format
  const isEnhanced = isEnhancedSchedule(schedule);
  const isLegacy = isLegacySchedule(schedule);

  // Convert enhanced to legacy for basic tab
  const getLegacySchedule = useCallback((): LegacySchedule | null => {
    if (!schedule) return null;

    if (isLegacy) {
      return schedule;
    }

    if (isEnhanced) {
      const enhanced = schedule;
      // Convert enhanced back to legacy format
      const activeDays = Object.entries(enhanced.regular_hours)
        .filter(([_, hours]) => hours !== null)
        .map(([day, _]) => {
          // Capitalize first letter
          return day.charAt(0).toUpperCase() + day.slice(1);
        });

      // Get first non-null hours as the default
      const firstHours = Object.values(enhanced.regular_hours).find(h => h !== null);

      return {
        start_date: enhanced.season_dates?.start || '',
        end_date: enhanced.season_dates?.end || '',
        days: activeDays,
        hours: firstHours || { start: '18:00', end: '22:00' }
      };
    }

    return null;
  }, [schedule, isLegacy, isEnhanced]);

  // Convert legacy to enhanced for advanced tab
  const getEnhancedSchedule = useCallback((): EnhancedSchedule => {
    const currentYear = new Date().getFullYear();
    const detectedTimezone = detectTimezone(city, state);

    if (isEnhanced) {
      return schedule;
    }

    if (isLegacy) {
      const legacy = schedule;
      const enhanced: EnhancedSchedule = {
        timezone: detectedTimezone,
        regular_hours: {},
        season_dates: {
          start: legacy.start_date,
          end: legacy.end_date
        }
      };

      // Initialize all days as null
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      dayKeys.forEach(day => {
        enhanced.regular_hours[day] = null;
      });

      // Set hours for scheduled days
      legacy.days.forEach(dayName => {
        const dayKey = dayName.toLowerCase();
        enhanced.regular_hours[dayKey] = {
          start: legacy.hours.start,
          end: legacy.hours.end
        };
      });

      return enhanced;
    }

    // Create new enhanced schedule if none exists
    return {
      timezone: detectedTimezone,
      regular_hours: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null
      },
      season_dates: {
        start: `${currentYear}-11-24`,
        end: `${currentYear + 1}-01-06`
      }
    };
  }, [schedule, isEnhanced, isLegacy, city, state]);

  // Handle basic tab changes (legacy format)
  const handleBasicChange = useCallback((updatedSchedule: LegacySchedule) => {
    onChange(updatedSchedule);
  }, [onChange]);

  // Handle advanced tab changes (enhanced format)
  const handleAdvancedChange = useCallback((updatedSchedule: EnhancedSchedule) => {
    onChange(updatedSchedule);
  }, [onChange]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Schedule & Hours
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'basic' | 'advanced')}>
          <TabsList className="grid w-full" style={{ gridTemplateColumns: showAdvanced ? '1fr 1fr' : '1fr' }}>
            <TabsTrigger value="basic">
              <Clock className="h-4 w-4 mr-2" />
              Basic
            </TabsTrigger>
            {showAdvanced && (
              <TabsTrigger value="advanced">
                <Calendar className="h-4 w-4 mr-2" />
                Advanced
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="basic" className="mt-6">
            <BasicScheduleTab
              schedule={getLegacySchedule()}
              onChange={handleBasicChange}
            />
          </TabsContent>

          {showAdvanced && (
            <TabsContent value="advanced" className="mt-6">
              <AdvancedScheduleTab
                schedule={getEnhancedSchedule()}
                city={city}
                state={state}
                onChange={handleAdvancedChange}
              />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default UnifiedScheduleSection;
